import React, { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import Checkbox from '../../Form/Checkbox';
import { colors, fontSize } from '../../../global/variables';
import { StyledField, StyledLabel, StyledInput, StyledSelect } from './FleetEditorStyled';
import {
  TRAFFIC_MODES,
  DEFAULT_TRAFFIC_MODE,
  IGNORE_ROUTE_VIOLATIONS_OPTIONS,
  buildTrafficRoutingPayload,
  fromDepartureTimeIso,
} from '../../../utils/avoidAreaHelpers';

const { secondaryTextColor, primaryTextColor, secondaryBorderColor } = colors;
const { small, smaller } = fontSize;

const StyledSection = styled.details({
  backgroundColor: 'rgba(0,0,0,.025)',
  border: `1px solid ${secondaryBorderColor}`,
  borderRadius: '0.12rem',
  padding: '0.75rem',
  marginBottom: '0.9rem',
});

const StyledSummary = styled.summary({
  fontSize: small,
  fontWeight: 700,
  color: primaryTextColor,
  cursor: 'pointer',
});

const StyledSummaryHint = styled.span({
  fontSize: smaller,
  fontWeight: 400,
  color: secondaryTextColor,
  marginLeft: '0.3rem',
});

const StyledFieldRow = styled.div({
  marginTop: '0.75rem',
});

const StyledSubTitle = styled.div({
  fontSize: smaller,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: secondaryTextColor,
  marginTop: '0.75rem',
  marginBottom: '0.4rem',
});

const StyledCheckboxColumn = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
});

const TrafficRoutingEditor = ({
  traffic,
  departureTime,
  ignoreRouteViolations,
  onChange,
  labels: {
    trafficRoutingTitle,
    trafficRoutingHint,
    trafficModeLabel,
    trafficModeLabels,
    departureTimeLabel,
    ignoreRouteViolationsLabel,
    ignoreRouteViolationsLabels,
  },
}) => {
  const [trafficMode, setTrafficMode] = useState(traffic?.mode || DEFAULT_TRAFFIC_MODE);
  const [departureTimeValue, setDepartureTimeValue] = useState(
    fromDepartureTimeIso(departureTime) || '',
  );
  const [violations, setViolations] = useState(ignoreRouteViolations || []);

  useEffect(() => {
    onChange(
      buildTrafficRoutingPayload({
        trafficMode,
        departureTime: departureTimeValue,
        ignoreRouteViolations: violations,
      }),
    );
  }, [trafficMode, departureTimeValue, violations, onChange]);

  const onToggleViolation = useCallback((violation) => {
    setViolations((prev) => (prev.includes(violation) ? [] : [violation]));
  }, []);

  return (
    <StyledSection>
      <StyledSummary>
        {trafficRoutingTitle}
        <StyledSummaryHint>{trafficRoutingHint}</StyledSummaryHint>
      </StyledSummary>
      <StyledFieldRow>
        <StyledField>
          <StyledLabel htmlFor="select-fleet-profile-traffic-mode">{trafficModeLabel}</StyledLabel>
          <StyledSelect
            id="select-fleet-profile-traffic-mode"
            value={trafficMode}
            onChange={(e) => setTrafficMode(e.target.value)}
          >
            {TRAFFIC_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {trafficModeLabels[mode]}
              </option>
            ))}
          </StyledSelect>
        </StyledField>
      </StyledFieldRow>
      <StyledFieldRow>
        <StyledField>
          <StyledLabel htmlFor="input-fleet-profile-departure-time">
            {departureTimeLabel}
          </StyledLabel>
          <StyledInput
            id="input-fleet-profile-departure-time"
            type="datetime-local"
            value={departureTimeValue}
            onChange={(e) => setDepartureTimeValue(e.target.value)}
          />
        </StyledField>
      </StyledFieldRow>
      <StyledSubTitle>{ignoreRouteViolationsLabel}</StyledSubTitle>
      <StyledCheckboxColumn>
        {IGNORE_ROUTE_VIOLATIONS_OPTIONS.map((violation) => (
          <Checkbox
            key={violation}
            id={`checkbox-fleet-profile-ignore-route-violation-${violation}`}
            value={violations.includes(violation)}
            label={ignoreRouteViolationsLabels[violation]}
            onChange={() => onToggleViolation(violation)}
          />
        ))}
      </StyledCheckboxColumn>
    </StyledSection>
  );
};

export default TrafficRoutingEditor;
